import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import ProfilePicture from './ProfilePicture';
import {
  MessageCircle,
  Clock,
  User,
  CheckCircle,
  AlertCircle,
  Calendar,
  Package,
  MessageSquare,
  Trash2,
  Archive,
  ArchiveRestore,
  MoreVertical
} from 'lucide-react';
import api from '../utils/api';

const MessageList = ({ messages, loading, onMarkRead, users, type, onDelete, onArchive, onMessageClick }) => {
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [processingActions, setProcessingActions] = useState(new Set());

  const getUserById = (userId) => {
    return users.find(user => user.id === userId) || null;
  };

  const getCurrentUser = () => {
    const user = JSON.parse(localStorage.getItem('exled_user'));
    return user;
  };

  const canManageMessage = (message) => {
    const currentUser = getCurrentUser();
    return currentUser && (
      message.sender_id === currentUser.id || 
      message.recipient_id === currentUser.id ||
      currentUser.role === 'admin'
    );
  };

  const handleDelete = async (messageId, e) => {
    console.log('🗑️ Delete button clicked for message:', messageId);
    e.stopPropagation();
    e.preventDefault();

    if (!window.confirm('Are you sure you want to delete this message?')) {
      console.log('❌ User cancelled delete');
      return;
    }

    console.log('✅ User confirmed delete, calling onDelete callback');
    try {
      setProcessingActions(prev => new Set(prev).add(messageId));
      // Call the parent's delete handler which will make the API call
      onDelete?.(messageId);
    } catch (error) {
      console.error('❌ Error in delete process:', error);
      alert('Failed to delete message');
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  const handleArchive = async (messageId, e) => {
    e.stopPropagation();
    try {
      setProcessingActions(prev => new Set(prev).add(messageId));
      // Call the parent's archive handler which will make the API call
      onArchive?.(messageId);
    } catch (error) {
      console.error('Error archiving message:', error);
      alert('Failed to archive message');
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  const handleRestore = async (messageId, e) => {
    e.stopPropagation();
    try {
      setProcessingActions(prev => new Set(prev).add(messageId));
      const response = await api.put(`/messages/${messageId}/restore`);

      if (response.status === 200) {
        onArchive?.(messageId); // Use the same callback to remove from archived list
      } else {
        throw new Error('Failed to restore message');
      }
    } catch (error) {
      console.error('Error restoring message:', error);
      alert('Failed to restore message');
    } finally {
      setProcessingActions(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  const getMessagePriorityHue = (message) => {
    if (!message.product_priority) {
      // Default blue gradient for regular messages
      return 'border-l-4 bg-gradient-to-r from-blue-500/10 via-blue-400/5 to-transparent border-blue-400';
    }
    
    switch (message.product_priority) {
      case 'high':
        return 'border-l-4 bg-gradient-to-r from-red-500/15 via-red-400/8 to-transparent border-red-400 shadow-sm';
      case 'medium':
        return 'border-l-4 bg-gradient-to-r from-orange-500/15 via-orange-400/8 to-transparent border-orange-400 shadow-sm';
      case 'low':
        return 'border-l-4 bg-gradient-to-r from-green-500/15 via-green-400/8 to-transparent border-green-400 shadow-sm';
      default:
        return 'border-l-4 bg-gradient-to-r from-blue-500/10 via-blue-400/5 to-transparent border-blue-400';
    }
  };

  const getTaskActionIcon = (taskAction) => {
    switch (taskAction) {
      case 'please_comment':
        return MessageSquare;
      case 'please_progress':
        return AlertCircle;
      case 'please_order':
        return Package;
      case 'schedule_meeting':
        return Calendar;
      default:
        return MessageCircle;
    }
  };

  const getTaskActionColor = (taskAction) => {
    switch (taskAction) {
      case 'please_comment':
        return 'text-blue-600 bg-blue-100';
      case 'please_progress':
        return 'text-orange-600 bg-orange-100';
      case 'please_order':
        return 'text-purple-600 bg-purple-100';
      case 'schedule_meeting':
        return 'text-green-600 bg-green-100';
      default:
        return 'text-slate-600 bg-slate-100';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('en-US', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const handleMarkRead = (message) => {
    if (type === 'inbox' && !message.is_read && onMarkRead) {
      onMarkRead(message.id);
    }
  };

  const handleMessageClick = (message) => {
    // Mark as read when clicked
    if (type === 'inbox' && !message.is_read && onMarkRead) {
      onMarkRead(message.id);
    }
    
    // Navigate to product and specific note if message click handler is provided
    if (onMessageClick) {
      onMessageClick(message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading messages...</p>
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="p-4 bg-slate-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <MessageCircle className="w-8 h-8 text-slate-400" />
        </div>
        <h3 className="text-lg font-medium text-slate-900 mb-2">
          No messages
        </h3>
        <p className="text-slate-600">
          {type === 'inbox' 
            ? "You don't have any messages yet." 
            : "You haven't sent any messages yet."
          }
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {messages.map((message) => {
        const sender = type === 'inbox' 
          ? getUserById(message.sender_id)
          : getUserById(message.recipient_id);
        
        if (!sender) {
          return null; // or a placeholder
        }

        const TaskIcon = getTaskActionIcon(message.task_action);
        const taskActionColor = getTaskActionColor(message.task_action);

        return (
          <Card 
            key={message.id} 
            className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
              type === 'inbox' && !message.is_read 
                ? 'bg-blue-50 border-blue-200 shadow-sm' 
                : 'hover:bg-slate-50'
            } ${getMessagePriorityHue(message) || ''}`}
            onClick={() => handleMessageClick(message)}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between space-x-4">
                <div className="flex items-start space-x-3 flex-1">
                  {/* Avatar or Icon */}
                  <div className="flex-shrink-0 w-8 h-8">
                    {message.message_type === 'task_request' ? (
                      <div className={`p-2 rounded-lg ${taskActionColor}`}>
                        <TaskIcon className="w-4 h-4" />
                      </div>
                    ) : (
                      <ProfilePicture 
                        src={sender.profile_picture_url} 
                        alt={sender.full_name} 
                        size="sm" 
                      />
                    )}
                  </div>

                  {/* Message Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`font-medium ${
                        type === 'inbox' && !message.is_read 
                          ? 'text-slate-900' 
                          : 'text-slate-700'
                      }`}>
                        {type === 'inbox' ? `From: ${sender.full_name}` : `To: ${sender.full_name}`}
                      </span>
                      <span className="text-xs text-slate-500">
                        @{sender.username}
                      </span>
                      {message.message_type === 'task_request' && (
                        <Badge variant="secondary" className="text-xs">
                          Task
                        </Badge>
                      )}
                    </div>

                    <h4 className={`font-medium mb-2 ${
                      type === 'inbox' && !message.is_read 
                        ? 'text-slate-900' 
                        : 'text-slate-700'
                    }`}>
                      {message.subject}
                    </h4>

                    <p className="text-sm text-slate-600 line-clamp-2">
                      {message.content}
                    </p>

                    {message.product_id && (
                      <div className="mt-2 flex items-center space-x-1 text-xs text-slate-500">
                        <Package className="w-3 h-3" />
                        <span>Related to product</span>
                        {message.stage && (
                          <>
                            <span>•</span>
                            <span className="capitalize">
                              {message.stage.replace('_', ' ')} stage
                            </span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Timestamp and Status */}
                <div className="flex-shrink-0 text-right">
                  <div className="flex items-center justify-between space-x-2">
                    <div className="flex items-center space-x-1 text-xs text-slate-500">
                      <Clock className="w-3 h-3" />
                      <span>{formatDate(message.created_at)}</span>
                    </div>
                    
                    {canManageMessage(message) && (
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-slate-400 hover:text-slate-600"
                          onClick={(e) => {
                            console.log('📋 Three dots button clicked for message:', message.id);
                            e.stopPropagation();
                            const newDropdownState = dropdownOpen === message.id ? null : message.id;
                            console.log('📋 Setting dropdown state to:', newDropdownState);
                            setDropdownOpen(newDropdownState);
                          }}
                        >
                          <MoreVertical className="w-3 h-3" />
                        </Button>
                        
                        {dropdownOpen === message.id && (
                          <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
                            {type === 'archived' ? (
                              <button
                                onClick={(e) => handleRestore(message.id, e)}
                                disabled={processingActions.has(message.id)}
                                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2 disabled:opacity-50"
                              >
                                <ArchiveRestore className="w-3 h-3" />
                                <span>Restore</span>
                              </button>
                            ) : (
                              <button
                                onClick={(e) => handleArchive(message.id, e)}
                                disabled={processingActions.has(message.id)}
                                className="w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 flex items-center space-x-2 disabled:opacity-50"
                              >
                                <Archive className="w-3 h-3" />
                                <span>Archive</span>
                              </button>
                            )}
                            <button
                              onClick={(e) => handleDelete(message.id, e)}
                              disabled={processingActions.has(message.id)}
                              className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2 disabled:opacity-50"
                            >
                              <Trash2 className="w-3 h-3" />
                              <span>Delete</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {type === 'inbox' && (
                    <div className="flex items-center justify-end mt-1">
                      {message.is_read ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : (
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export default MessageList;